/**
 * useShare Hook
 *
 * Reusable hook for social sharing functionality
 * - Fetches daily share status
 * - Handles Twitter redirect with referral link
 * - Tracks share and awards points
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getShareStatus,
  shareChestResult,
  shareJunkResult,
  shareLotteryResult,
  type ShareType,
} from '@/api/share';
import { getInviteInfo } from '@/api/referral';

const SHARE_POINTS = {
  chest: 100,
  lottery: 100,
  junk: 20,
};

export function useShare() {
  const queryClient = useQueryClient();
  const [isSharing, setIsSharing] = useState(false);

  // Fetch daily share status
  const { data: shareStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['share-status'],
    queryFn: async () => {
      const response = await getShareStatus();
      return response.data;
    },
    staleTime: 60 * 1000, // 1 minute - balance between freshness and performance
    refetchOnMount: true, // Refetch if stale (default behavior)
  });

  // Fetch user's invite code for referral link
  const { data: inviteInfo } = useQuery({
    queryKey: ['invite-info'],
    queryFn: async () => {
      const response = await getInviteInfo();
      return response.data;
    },
  });

  // Track share mutation
  const trackShareMutation = useMutation({
    mutationFn: async (shareType: ShareType) => {
      switch (shareType) {
        case 'chest':
          return shareChestResult();
        case 'junk':
          return shareJunkResult();
        case 'lottery':
          return shareLotteryResult();
        default:
          throw new Error(`Unknown share type: ${shareType}`);
      }
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data
      // The button logic (sharePoints > 0) ensures correct display
      queryClient.invalidateQueries({ queryKey: ['share-status'] });
      queryClient.invalidateQueries({ queryKey: ['user-points'] });
    },
  });

  /**
   * Get referral link for sharing
   */
  const getReferralLink = useCallback(() => {
    if (!inviteInfo?.invite_code) {
      return 'https://junk.fun';
    }
    return `https://junk.fun?ref=${inviteInfo.invite_code}`;
  }, [inviteInfo]);

  /**
   * Generate tweet text for chest/lottery wins
   */
  const getChestTweetText = useCallback(
    (points: number, solCredits?: number) => {
      const referralLink = getReferralLink();
      if (solCredits && solCredits > 0) {
        return `Opened Chests on @junkfun_ and won ${solCredits.toFixed(4)} SOL Credits + ${points} points! Junk your dead memes to start winning: ${referralLink}`;
      }
      return `Opened Chests on @junkfun_ and just earned ${points} points! Junk your dead memes to start earning points too: ${referralLink}`;
    },
    [getReferralLink]
  );

  /**
   * Generate tweet text for junk/burn results
   */
  const getJunkTweetText = useCallback(
    (points: number) => {
      const referralLink = getReferralLink();
      return `Got rid of my dead memecoins and earned ${points} points with @junkfun_! You can earn points with your dead memes too: ${referralLink}`;
    },
    [getReferralLink]
  );

  /**
   * Generate tweet text for lottery wins
   */
  const getLotteryTweetText = useCallback(
    (prizeSol: number) => {
      const referralLink = getReferralLink();
      return `I just won ${prizeSol.toFixed(4)} SOL with @junkfun_'s Points Lottery! Try your luck for the next drawing: ${referralLink}`;
    },
    [getReferralLink]
  );

  /**
   * Open Twitter with pre-filled tweet and track share
   */
  const shareOnTwitter = useCallback(
    async (shareType: ShareType, tweetText: string) => {
      console.log('🔍 [useShare] Starting share:', { shareType });
      setIsSharing(true);
      try {
        // Track the share FIRST (awards points if not shared today)
        console.log('🔍 [useShare] Calling track share API...');
        const result = await trackShareMutation.mutateAsync(shareType);
        console.log('✅ [useShare] Track share result:', result.data);

        // Then open Twitter with pre-filled tweet
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
        console.log('🔍 [useShare] Opening Twitter:', twitterUrl);
        window.open(twitterUrl, '_blank', 'width=550,height=420');

        return result.data;
      } catch (error) {
        console.error('❌ [useShare] Failed to share:', error);
        if (error instanceof Error) {
          console.error('❌ [useShare] Error message:', error.message);
          console.error('❌ [useShare] Error stack:', error.stack);
        }
        throw error;
      } finally {
        setIsSharing(false);
      }
    },
    [trackShareMutation]
  );

  /**
   * Share chest result
   */
  const shareChest = useCallback(
    async (points: number, solCredits?: number) => {
      const tweetText = getChestTweetText(points, solCredits);
      return shareOnTwitter('chest', tweetText);
    },
    [getChestTweetText, shareOnTwitter]
  );

  /**
   * Share junk/burn result
   */
  const shareJunk = useCallback(
    async (points: number) => {
      const tweetText = getJunkTweetText(points);
      return shareOnTwitter('junk', tweetText);
    },
    [getJunkTweetText, shareOnTwitter]
  );

  /**
   * Share lottery result
   */
  const shareLottery = useCallback(
    async (prizeSol: number) => {
      const tweetText = getLotteryTweetText(prizeSol);
      return shareOnTwitter('lottery', tweetText);
    },
    [getLotteryTweetText, shareOnTwitter]
  );

  /**
   * Check if user can share for a specific type today
   */
  const canShare = useCallback(
    (shareType: ShareType) => {
      if (!shareStatus) return false;
      return shareStatus[shareType] === true;
    },
    [shareStatus]
  );

  /**
   * Get potential points for sharing (if not shared today)
   */
  const getPotentialPoints = useCallback(
    (shareType: ShareType) => {
      if (canShare(shareType)) {
        return SHARE_POINTS[shareType];
      }
      return 0;
    },
    [canShare]
  );

  return {
    // Share status
    shareStatus,
    isLoadingStatus,
    canShare,
    getPotentialPoints,

    // Share actions
    shareChest,
    shareJunk,
    shareLottery,
    isSharing,

    // Tweet text generators (for custom use)
    getChestTweetText,
    getJunkTweetText,
    getLotteryTweetText,
    getReferralLink,
  };
}
